// components/AccountingReportsPage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TrialBalance from './TrialBalance';
import BalanceSheet from './BalanceSheet';
import IncomeStatement from './IncomeStatement';
import GeneralLedger from './GeneralLedger';
import CashFlowStatement from './CashFlowStatement';
import IncomeStatementComparative from './IncomeStatementComparative';
import Worksheet from './Worksheet';
import VATReport from './VATReport';
import ARAgingReport from './ARAgingReport';
import APAgingReport from './APAgingReport';
import Layout from './Layout';

const VALID_TABS = [
  'trial-balance',
  'balance-sheet',
  'income-statement',
  'general-ledger',
  'cash-flow',
  'worksheet',
  'vat-report',
  'ar-aging',
  'ap-aging',
];

const AccountingReportsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromSearch = (search) => {
    const param = new URLSearchParams(search).get('report');
    return VALID_TABS.includes(param) ? param : 'trial-balance';
  };

  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));

  useEffect(() => {
    setActiveTab(getTabFromSearch(location.search));
  }, [location.search]);

  const handleTabClick = (tabId) => {
    navigate(`/accounting-reports?report=${tabId}`, { replace: true });
  };

  const tabs = [
    { id: 'trial-balance',   name: 'Trial Balance' },
    { id: 'balance-sheet',   name: 'Balance Sheet' },
    { id: 'income-statement', name: 'Income Statement' },
    { id: 'general-ledger',  name: 'General Ledger' },
    { id: 'cash-flow',       name: 'Cash Flow Statement' },
    { id: 'worksheet',       name: 'Worksheet' },
    { id: 'vat-report',      name: 'VAT Report' },
    { id: 'ar-aging',        name: 'AR Aging' },
    { id: 'ap-aging',        name: 'AP Aging' },
  ];

  const activeTabName = tabs.find((tab) => tab.id === activeTab)?.name || 'Accounting Reports';

  const renderContent = () => {
    switch (activeTab) {
      case 'trial-balance':    return <TrialBalance />;
      case 'balance-sheet':    return <BalanceSheet />;
      case 'income-statement': return <IncomeStatementComparative />;
      case 'general-ledger':   return <GeneralLedger />;
      case 'cash-flow':        return <CashFlowStatement />;
      case 'worksheet':        return <Worksheet />;
      case 'vat-report':       return <VATReport />;
      case 'ar-aging':         return <ARAgingReport />;
      case 'ap-aging':         return <APAgingReport />;
      default:                 return <TrialBalance />;
    }
  };

  return (
    <Layout pageTitle={activeTabName}>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Accounting Reports</h1>
            <p className="text-gray-600 mt-2">Financial reports and statements</p>
          </div>

          {/* Tabs */}
          <div className="mb-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-teal-500 text-teal-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          {renderContent()}
        </div>
      </div>
    </Layout>
  );
};

export default AccountingReportsPage;
